/**
 * CabinetTour — Онбордінг-тур кабінету пацієнта.
 *
 * Точно відповідає прототипу account-prototype.html:
 * - Темний overlay з "вирізаним" підсвіченим елементом (box-shadow техніка)
 * - White tooltip card з текстом, dot-навігацією, кнопками Назад/Далі
 * - Автоматична навігація між табами при переході між кроками
 * - Анімований перехід spot → element position
 */
import React, { useEffect, useRef, useCallback } from 'react';
import { TourStep, TourStepType } from '../../hooks/useTour';
import { NavTab } from '../layout/BottomNavbar';
import { useI18n } from '../../hooks/useI18n';
import { TranslationKeys } from '../../i18n';

interface CabinetTourProps {
  isActive: boolean;
  currentStep: number;
  steps: TourStep[];
  totalSteps: number;
  activeTab: NavTab;
  onChangeTab: (tab: NavTab) => void;
  onNext: () => void;
  onPrev: () => void;
  onEnd: () => void;
}

interface SpotRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TipPosition {
  top?: string;
  bottom?: string;
  left: string;
}

const PAD = 6; // padding around highlighted element

function findTarget(stepType: TourStepType): Element | null {
  if (stepType.type === 'full') return null;

  if (stepType.type === 'selector') {
    const els = document.querySelectorAll(stepType.selector);
    for (let i = 0; i < els.length; i++) {
      const el = els[i] as HTMLElement;
      // Return the first visible element (works for desktop vs mobile nav)
      if (el.offsetWidth > 0 || el.offsetHeight > 0) return el;
    }
  }

  return null;
}

export const CabinetTour: React.FC<CabinetTourProps> = ({
  isActive,
  currentStep,
  steps,
  totalSteps,
  activeTab,
  onChangeTab,
  onNext,
  onPrev,
  onEnd,
}) => {
  const { t } = useI18n();
  const spotRef = useRef<HTMLDivElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const positionedRef = useRef(false);

  const step = steps[currentStep];

  const positionTour = useCallback(() => {
    if (!isActive || !step || !spotRef.current || !tipRef.current) return;

    const spot = spotRef.current;
    const tip = tipRef.current;

    if (step.stepType.type === 'full') {
      // Welcome step: no highlight, center the tooltip
      spot.style.top = '50%';
      spot.style.left = '50%';
      spot.style.width = '0px';
      spot.style.height = '0px';
      spot.style.borderRadius = '0';

      tip.style.top = '34%';
      tip.style.bottom = 'auto';
      tip.style.left = '50%';
      tip.style.transform = 'translateX(-50%)';
      positionedRef.current = true;
      return;
    }

    const el = findTarget(step.stepType);
    if (!el) {
      // Element not found (e.g., bell-btn hidden in non-PWA mode) — skip this step
      onNext();
      return;
    }

    // Scroll element into view then measure
    // IMPORTANT: removed behavior: 'smooth' because it breaks getBoundingClientRect calculation
    // if we measure while it's still animating.
    el.scrollIntoView({ block: 'center' });

    setTimeout(() => {
      const r = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      const spotRect: SpotRect = {
        top: r.top - PAD,
        left: r.left - PAD,
        width: r.width + PAD * 2,
        height: r.height + PAD * 2,
      };

      spot.style.top = `${spotRect.top}px`;
      spot.style.left = `${spotRect.left}px`;
      spot.style.width = `${spotRect.width}px`;
      spot.style.height = `${spotRect.height}px`;
      spot.style.borderRadius = '14px';

      // Position tooltip: below element if in top half, above if in bottom half
      const tipW = 270;
      const isBelow = spotRect.top + spotRect.height < vh * 0.55;

      const tipPos: TipPosition = {
        left: `${Math.max(12, Math.min(spotRect.left + spotRect.width / 2 - tipW / 2, vw - tipW - 12))}px`,
      };

      if (isBelow) {
        tipPos.top = `${spotRect.top + spotRect.height + 12}px`;
        tip.style.bottom = 'auto';
      } else {
        const tipHeight = tip.offsetHeight || 160;
        tipPos.top = `${spotRect.top - tipHeight - 12}px`;
        tip.style.bottom = 'auto';
      }

      tip.style.top = tipPos.top || 'auto';
      tip.style.left = tipPos.left;
      tip.style.transform = 'none'; // Clear translate used in full step
      positionedRef.current = true;
    }, 40);
  }, [isActive, step, onEnd]);

  // When step changes: first switch tab if needed, then position
  useEffect(() => {
    if (!isActive || !step) return;
    positionedRef.current = false;

    if (activeTab !== step.tab) {
      onChangeTab(step.tab);
      // Wait for tab switch + render
      const timer = setTimeout(positionTour, 200);
      return () => clearTimeout(timer);
    } else {
      const timer = setTimeout(positionTour, 80);
      return () => clearTimeout(timer);
    }
  }, [isActive, currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reposition on window resize
  useEffect(() => {
    if (!isActive) return;
    const handler = () => positionTour();
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [isActive, positionTour]);

  if (!isActive || !step) return null;

  const isFirst = currentStep === 0;
  const isLast = currentStep === totalSteps - 1;

  return (
    <>
      <div className="cab-tour-overlay" onClick={onEnd} />
      <div
        ref={spotRef}
        className="cab-tour-spot"
      />
      <div
        ref={tipRef}
        className="cab-tour-tip"
        onClick={e => e.stopPropagation()}
      >
        <button className="cab-tour-x" onClick={onEnd} aria-label="Zamknij">✕</button>
        <p className="cab-tour-text">{t(step.textKey as keyof TranslationKeys)}</p>
        <div className="cab-tour-dots">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <i key={i} className={i === currentStep ? 'on' : ''} />
          ))}
        </div>
        <div className="cab-tour-btns">
          <button
            className="cab-tour-btn"
            onClick={onPrev}
            disabled={isFirst}
          >
            {t('tour_back')}
          </button>
          <button
            className="cab-tour-btn primary"
            onClick={isLast ? onEnd : onNext}
          >
            {isLast ? t('tour_finish') : t('tour_next')}
          </button>
        </div>
      </div>

      <style>{`
        /* ===== Onboarding Tour — відповідає прототипу ===== */
        .cab-tour-overlay {
          position: fixed;
          inset: 0;
          z-index: 200;
          background: transparent;
          pointer-events: all;
        }
        .cab-tour-spot {
          position: fixed;
          z-index: 201;
          border-radius: 14px;
          box-shadow: 0 0 0 9999px rgba(20, 10, 45, 0.62);
          transition: top 0.22s ease, left 0.22s ease, width 0.22s ease, height 0.22s ease, border-radius 0.22s ease;
          pointer-events: none;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
        }
        .cab-tour-tip {
          position: fixed;
          z-index: 202;
          background: #fff;
          border-radius: 18px;
          padding: 15px 15px 12px;
          box-shadow: 0 16px 44px rgba(20, 10, 45, 0.4);
          width: 270px;
          pointer-events: all;
          top: 34%;
          left: 50%;
          transform: translateX(-50%);
        }
        .cab-tour-x {
          position: absolute;
          top: 9px;
          right: 11px;
          background: none;
          border: none;
          font-size: 15px;
          color: #786798;
          cursor: pointer;
          line-height: 1;
          font-family: inherit;
          padding: 4px;
        }
        .cab-tour-x:hover {
          color: #361777;
        }
        .cab-tour-text {
          font-size: 13.5px;
          line-height: 1.5;
          color: #57437E;
          margin: 0 18px 12px 0!important;
          font-family: 'Poppins', sans-serif;
          font-weight: 500;
        }
        .cab-tour-dots {
          display: flex;
          gap: 5px;
          justify-content: center;
          margin-bottom: 12px;
        }
        .cab-tour-dots i {
          display: block;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #DDD2F0;
          transition: width 0.2s ease, background 0.2s ease, border-radius 0.2s ease;
          font-style: normal;
        }
        .cab-tour-dots i.on {
          width: 17px;
          border-radius: 6px;
          background: #361777;
        }
        .cab-tour-btns {
          display: flex;
          gap: 8px;
        }
        .cab-tour-btn {
          flex: 1;
          height: 38px;
          border-radius: 100px;
          font-family: 'Poppins', sans-serif;
          font-weight: 700;
          font-size: 13px;
          cursor: pointer;
          border: 1.5px solid #E6D6FA;
          background: #fff;
          color: #361777;
          transition: opacity 0.15s;
        }
        .cab-tour-btn.primary {
          background: #361777;
          color: #fff;
          border-color: #361777;
        }
        .cab-tour-btn:disabled {
          opacity: 0.4;
          cursor: default;
        }
        .cab-tour-btn:not(:disabled):hover {
          opacity: 0.85;
        }
      `}</style>
    </>
  );
};
